/**
 * i18n setup — bilingual (English + Arabic) with RTL toggle.
 *
 * To roll back the entire feature: delete this folder, remove the
 * import from main.tsx, and remove the language switcher button.
 * No other code changes needed — useTranslation falls back gracefully.
 */

import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';
import ar from './locales/ar.json';

export const SUPPORTED_LANGUAGES = ['en', 'ar'] as const;
export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

export const RTL_LANGUAGES: SupportedLanguage[] = ['ar'];

export function isRtl(lang: string): boolean {
  return RTL_LANGUAGES.includes(lang.split('-')[0] as SupportedLanguage);
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      ar: { translation: ar },
    },
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES,
    // Browser language detection commonly returns regional tags such as ar-SA.
    // We only ship language-level resources, so resolve those tags to `ar`
    // instead of falling through to English.
    load: 'languageOnly',
    nonExplicitSupportedLngs: true,
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'ryze_lang',
      caches: ['localStorage'],
    },
    interpolation: {
      escapeValue: false, // React already escapes
    },
  });

// Keep <html dir> and <html lang> in sync with the active language
function applyDirection(lang: string): void {
  const resolvedLang = lang.split('-')[0];
  const dir = isRtl(resolvedLang) ? 'rtl' : 'ltr';
  document.documentElement.setAttribute('dir',  dir);
  document.documentElement.setAttribute('lang', resolvedLang);
}

if (typeof document !== 'undefined') {
  applyDirection(i18n.language);
  i18n.on('languageChanged', applyDirection);
}

export default i18n;
